<template>
  <div class="contact-boxes">
      <div class="recipents-list">
          <span
              v-for="contact in props.contacts_boxes_collection.items" :key="contact.id"
              class="recipient"
              :class="{ valid: contact.email , notValid : !contact.email }"
              role="button"
              :title="contact.email ? contact.email : contact.name"
              v-drag-start="{
                data: {
                  from: props.contact_input_id,
                  item: contact
                },
                event: 'drag-contact-boxes'
              }"
              v-on:dblclick="onContactBoxDoubleClick(contact)"
              v-context-menu="{action: onContactBoxContextMenu, data : contact }"
          >
          {{contact.name || contact.email}}
          <span @click="onContactBoxDelete(contact)"><i class="fas fa-times-circle"></i></span></span>
          <input
              size="5"
              ref="input_element"
              type="text"
              autocomplete="off"
              v-model="input_model"
              v-focus="input_focus"
              @keyup="onContactInputKeyUp($event)"
              @keydown="onContactInputKeyDown($event)"
              @paste.prevent="onContactInputPaste($event)"
              @focus="onContactInputFocus()"
              @blur="onContactInputBlur()"
          >
      </div>

      <ContactsInputBoxesAutoComplete v-if="auto_complete_visibility"
          ref="auto_complete_component"
          @contactsAutoCompletedSelected="onAutoCompleteContactSelected"
          :contacts_controller="contacts_controller"
          :auto_complete_search="input_model"
      >
      </ContactsInputBoxesAutoComplete>
  </div>

  <ContextMenu ref="context_menu_component">
      <template #menu_items>
          <ContactsContextBoxes
            :context_contact="email_box_data"
            :disabled_actions="{'delete' : true}"
            />
      </template>
  </ContextMenu>
</template>

<script setup>
  import {ref}                                    from 'vue';
  import {defineProps, defineExpose, defineEmits} from 'vue';
  import ContactsInputBoxesAutoComplete           from '@/vue/Views/Contacts/Partials/ContactsInputBoxesAutoComplete.vue';
  import ContextMenu                              from '@/vue/Elements/Tools/ContextMenu.vue';
  import ContactsContextBoxes                     from './ContactsContextBoxes.vue';

  const emit = defineEmits([
    'inputFocus',
  ])

  const props = defineProps({
    contacts_boxes_collection : {
      default : [],
    },

    contacts_controller : {
      required: true,
    },

    contact_input_id: {
      default: null
    },

    input_focus: {
      default: false
    }
  })

  const input_model                = ref("")
  const input_element              = ref()
  const context_menu_component     = ref();
  const auto_complete_component    = ref();
  const auto_complete_visibility   = ref(false)
  const backspace_enabled          = ref(false);
  const email_box_data             = ref({})
  const disabled_keys_autocomplete = {
    'ArrowDown' : true ,
    'ArrowUp'   : true
  }

  const onContactBoxContextMenu = (data) => {
    emailBoxDataSetValue(data)
    context_menu_component.value.show();
  }

  const onContactBoxDoubleClick = (contact) => {
    if (input_model.value.length > 0) {
      props.contacts_boxes_collection.addPersonsByString(input_model.value);
    }

    setInputModel(contact)
    input_element.value.focus();
    props.contacts_boxes_collection.delete(contact.id);
  }

  const onContactBoxDelete = (contact) => {
    props.contacts_boxes_collection.delete(contact.id);
  }

  const onContactInputPaste = (event) => {
    let input = input_model.value + event.clipboardData.getData('text')
    clearInputModel()
    props.contacts_boxes_collection.addPersonsByString(input);
  }

  const onContactInputBlur = () => {
    autoCompleteVisibility(false);
  }

  const onContactInputFocus = () => {
    emit('inputFocus');
    autoCompleteVisibility(true);
  }

  const onContactInputKeyUp = (event) => {
    if (input_model.value.length === 0) {
      backspaceEnable(true);
    }
  }

  const onContactInputKeyDown = (event) => {
    if(disabled_keys_autocomplete[event.code] && auto_complete_component.value.isActive() === true) {
      event.preventDefault();
      return
    }

    let method = (!contact_input_keydown_methods[event.code]) ? 'Generic' : event.code;
    contact_input_keydown_methods[method](event);
  }

  const onContactInputGeneric = () => {
    backspaceEnable(false);
  }

  const onContactInputBackSpace = (event) => {
    if (props.contacts_boxes_collection.count === 0) {
      return;
    }

    if (backspace_enabled.value === false) {
      return;
    }

    event.preventDefault();
    setInputModel(props.contacts_boxes_collection.pop());
    backspaceEnable(false);
  }

  const onContactInputSpace = (event) => {
    if (!trimInputModel()) {
      event.preventDefault();
      return;
    }

    if (props.contacts_boxes_collection.addPersonByEmail(input_model.value) === false) {
      return;
    }

    clearInputModel();
  }

  const onContactInputActivator = (event) => {
    event.preventDefault();

    if (input_model.value.length === 0 || auto_complete_component.value.isActive() === true) {
      return;
    }

    props.contacts_boxes_collection.addPersonByType(input_model.value);
    clearInputModel()
  }

  const onContactInputActivatorForce = () => {
    if (!trimInputModel()) {
      return;
    }

    props.contacts_boxes_collection.addPersonByType(input_model.value);
    clearInputModel()
  }

  const onAutoCompleteContactSelected = (contact) => {
    clearInputModel()
    backspaceEnable(true)
    props.contacts_boxes_collection.add(contact);
  }

  const getContactsCollection = () => {
    return props.contacts_boxes_collection;
  }

  const resetContacts = () => {
    props.contacts_boxes_collection.reset();
  }

  const deleteContactsByEmail = (emails) => {
    props.contacts_boxes_collection.deletePersonsByEmail(emails);
  }

  const setContactsInCollection = (contacts) => {
    props.contacts_boxes_collection.reset();
    props.contacts_boxes_collection.addItems(contacts);
  }

  const setInputModel = (contact)  => {
    input_model.value = contact.email ? contact.email : contact.name
  }

  const clearInputModel = () => {
    input_model.value = ""
  }

  const trimInputModel = () => {
    return input_model.value.trim();
  }

  const backspaceEnable = (value) => {
    backspace_enabled.value = value;
  }

  const autoCompleteVisibility = (value) => {
    auto_complete_visibility.value = value;
  }

  const emailBoxDataSetValue = ( value ) => {
    email_box_data.value = value
  }

  const contact_input_keydown_methods = {
    'Enter'     : onContactInputActivator,
    'Comma'     : onContactInputActivator,
    'Space'     : onContactInputSpace,
    'Backspace' : onContactInputBackSpace,
    'Generic'   : onContactInputGeneric,
  }

  defineExpose({
    getContacts   : getContactsCollection,
    setContacts   : setContactsInCollection,
    deleteEmails  : deleteContactsByEmail,
    forceInput    : onContactInputActivatorForce,
    resetContacts : resetContacts,
  })
</script>